import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import type {
  PublicationListOut,
  SessionDetailOut,
} from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { PublicationIconLink } from '@/components/user_catalog/publications/PublicationIconLink';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { formatDate, formatSecondsClock } from '@/utils/format';
import { SESSION_SHOW_TABS } from '../constants';
import type { SessionShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<SessionShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface SessionShowHeaderProps {
  record: SessionDetailOut;
  publication?: PublicationListOut;
  activeTab: SessionShowTab;
  onTabChange: (key: string) => void;
}

export const SessionShowHeader = ({
  record,
  publication,
  activeTab,
  onTabChange,
}: SessionShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const coveragePct =
    record.product_duration > 0
      ? Math.round((record.covered_seconds / record.product_duration) * 100)
      : 0;

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Long titles wrap instead of pushing the tabs bar out. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            <PublicationIconLink publicationId={record.publication_id}>
              {publication?.title ?? `#${record.publication_id}`}
            </PublicationIconLink>
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t('progress.sessions.titles.show')} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('progress.sessions.fields.product_kind')}>
              <ProductFormatTag value={record.product_kind} labels="sessions" />
            </StatCell>
            <StatCell label={t('progress.sessions.fields.product_variant')}>
              <ProductVariantTag
                value={record.product_variant}
                labels="sessions"
              />
            </StatCell>
            <StatCell label={t('progress.sessions.sections.coverage')}>
              {`${formatSecondsClock(record.covered_seconds, { alwaysHours: true })} / ${formatSecondsClock(record.product_duration, { alwaysHours: true })} · ${coveragePct}%`}
            </StatCell>
            <StatCell label={t('progress.sessions.fields.entitlement')}>
              {record.entitlement ? (
                `${formatDate(record.entitlement.valid_from)} → ${formatDate(record.entitlement.valid_until)}`
              ) : (
                <Tag color="gold">
                  {t('progress.sessions.entitlement.none')}
                </Tag>
              )}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={SESSION_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
